import { useState, useCallback, useEffect } from 'react'
import type { CalendarEvent } from '../types'

export interface UseEventSelectionOptions {
  events: CalendarEvent[]
  onSelectionChange?: (selectedIds: string[]) => void
  multiSelect?: boolean
}

export interface UseEventSelectionReturn {
  selectedIds: string[]
  isSelected: (eventId: string) => boolean
  toggleSelection: (eventId: string, modifiers?: { ctrl?: boolean; shift?: boolean }) => void
  selectEvent: (eventId: string) => void
  deselectEvent: (eventId: string) => void
  selectAll: () => void
  clearSelection: () => void
  selectRange: (fromId: string, toId: string) => void
}

/**
 * Hook for managing event selection in the calendar
 * Supports single selection, multi-selection with Ctrl+Click, and range selection with Shift+Click
 */
export const useEventSelection = ({
  events,
  onSelectionChange,
  multiSelect = true,
}: UseEventSelectionOptions): UseEventSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)

  // Notify parent when selection changes
  useEffect(() => {
    onSelectionChange?.(selectedIds)
  }, [selectedIds, onSelectionChange])

  const isSelected = useCallback(
    (eventId: string) => {
      return selectedIds.includes(eventId)
    },
    [selectedIds]
  )

  const selectEvent = useCallback((eventId: string) => {
    setSelectedIds([eventId])
    setLastSelectedId(eventId)
  }, [])

  const deselectEvent = useCallback((eventId: string) => {
    setSelectedIds((prev) => prev.filter((id) => id !== eventId))
  }, [])

  const toggleSelection = useCallback(
    (eventId: string, modifiers: { ctrl?: boolean; shift?: boolean } = {}) => {
      if (!multiSelect) {
        // Single selection mode
        selectEvent(eventId)
        return
      }

      const { ctrl, shift } = modifiers

      // Shift+Click: Range selection
      if (shift && lastSelectedId && lastSelectedId !== eventId) {
        const lastIndex = events.findIndex((e) => e.id === lastSelectedId)
        const currentIndex = events.findIndex((e) => e.id === eventId)

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex)
          const end = Math.max(lastIndex, currentIndex)
          const rangeIds = events.slice(start, end + 1).map((e) => e.id)

          setSelectedIds((prev) => {
            // Merge with existing selection
            const newSelection = new Set([...prev, ...rangeIds])
            return Array.from(newSelection)
          })
          return
        }
      }

      // Ctrl+Click: Toggle single event in multi-selection
      if (ctrl) {
        setSelectedIds((prev) => {
          if (prev.includes(eventId)) {
            return prev.filter((id) => id !== eventId)
          } else {
            return [...prev, eventId]
          }
        })
        setLastSelectedId(eventId)
        return
      }

      // Regular click: Select only this event
      selectEvent(eventId)
    },
    [multiSelect, lastSelectedId, events, selectEvent]
  )

  const selectAll = useCallback(() => {
    const allIds = events.map((e) => e.id)
    setSelectedIds(allIds)
  }, [events])

  const clearSelection = useCallback(() => {
    setSelectedIds([])
    setLastSelectedId(null)
  }, [])

  const selectRange = useCallback(
    (fromId: string, toId: string) => {
      const fromIndex = events.findIndex((e) => e.id === fromId)
      const toIndex = events.findIndex((e) => e.id === toId)

      if (fromIndex !== -1 && toIndex !== -1) {
        const start = Math.min(fromIndex, toIndex)
        const end = Math.max(fromIndex, toIndex)
        const rangeIds = events.slice(start, end + 1).map((e) => e.id)
        setSelectedIds(rangeIds)
      }
    },
    [events]
  )

  return {
    selectedIds,
    isSelected,
    toggleSelection,
    selectEvent,
    deselectEvent,
    selectAll,
    clearSelection,
    selectRange,
  }
}
